/**
 * StrategyMiniChart Component
 *
 * Purpose: Reusable mini chart for displaying spread data with percentile reference lines
 * Features:
 * - Multiple modes: percentile, zscore, spread, session
 * - MT5-style reference lines (P25/P50/P75 or ±2σ)
 * - Real-time updates (appends new data points)
 * - Custom tooltip with percentile display
 * - Responsive width, fixed height (300px for 55% chart priority)
 *
 * @module Components/Strategy/StrategyMiniChart
 */

import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Box, Typography, ToggleButtonGroup, ToggleButton } from '@mui/material';
import PropTypes from 'prop-types';
import { getPercentileReferenceLines, getZScoreReferenceLines } from '../../utils/strategyCalculations';

/**
 * StrategyMiniChart Component
 *
 * @param {Object} props
 * @param {Array<Object>} props.data - Chart data array from formatChartData()
 * @param {string} props.mode - Chart mode: 'percentile', 'zscore', 'spread', 'session'
 * @param {Function} props.onModeChange - Callback when mode changes
 * @param {Object} props.percentileBands - Percentile bands for reference lines (required for percentile mode)
 * @param {Object} props.dailyStats - Daily statistics (spreadHigh/Low, betaHigh/Low, zScoreHigh/Low)
 * @param {number} props.height - Chart height in pixels (default: 300)
 * @param {number} props.currentSpread - Current spread value for highlighting
 * @param {number} props.currentBeta - Current beta coefficient value
 * @param {string} props.lineColor - Line color (default: cyan)
 */
const StrategyMiniChart = ({
    data = [],
    mode = 'percentile',
    onModeChange,
    percentileBands = null,
    dailyStats = null,
    height = 300,
    currentSpread = null,
    currentBeta = null,
    lineColor = '#06b6d4'
}) => {
    // Calculate reference lines based on mode
    const referenceLines = useMemo(() => {
        if (mode === 'percentile' && percentileBands) {
            return getPercentileReferenceLines(percentileBands);
        }
        if (mode === 'zscore') {
            return getZScoreReferenceLines();
        }
        return [];
    }, [mode, percentileBands]);

    // Custom label for CURRENT marker (white with subtle glow)
    const CurrentLabel = (props) => {
        const { x, y, value } = props;

        // Defensive check: Ensure coordinates are valid numbers
        if (typeof x !== 'number' || isNaN(x) ||
            typeof y !== 'number' || isNaN(y)) {
            return null;
        }

        return (
            <text
                x={x + 10}
                y={y - 10}
                fill="#ffffff"
                fontSize="14"
                fontWeight="900"
                letterSpacing="1.5"
                style={{
                    filter: 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.6))',
                    textShadow: '0 0 6px rgba(255, 255, 255, 0.6)'
                }}
            >
                {value}
            </text>
        );
    };

    // Custom label for P25/P50/P75 reference lines (simple text above line, no images)
    const PercentileLabel = (props) => {
        const { viewBox, value, stroke } = props;
        if (!viewBox) return null;

        const { x, y, width } = viewBox;

        // Defensive check: Ensure all coordinates are valid numbers
        if (typeof x !== 'number' || isNaN(x) ||
            typeof y !== 'number' || isNaN(y) ||
            typeof width !== 'number' || isNaN(width)) {
            return null;
        }

        return (
            <text
                x={x + width - 60}
                y={y - 8}
                fill={stroke}
                fontSize="10"
                fontWeight="600"
                textAnchor="end"
                stroke="rgba(17, 24, 39, 0.9)"
                strokeWidth="3"
                paintOrder="stroke fill"
                style={{ userSelect: 'none' }}
            >
                {value}
            </text>
        );
    };

    // Custom tooltip component
    const CustomTooltip = ({ active, payload }) => {
        if (!active || !payload || payload.length === 0) return null;

        const dataPoint = payload[0].payload;

        return (
            <Box sx={{
                backgroundColor: 'rgba(17, 24, 39, 0.95)',
                border: '1px solid rgba(6, 182, 212, 0.5)',
                borderRadius: '4px',
                padding: '10px 14px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
            }}>
                <Typography sx={{ color: '#06b6d4', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
                    {dataPoint.time}
                </Typography>
                <Typography sx={{ color: '#ffffff', fontSize: '13px', fontFamily: 'Courier New, monospace', fontWeight: '600' }}>
                    {dataPoint.label || `${dataPoint.value?.toFixed(2)}`}
                </Typography>
                {dataPoint.percentile && (
                    <Typography sx={{ color: '#f59e0b', fontSize: '11px', marginTop: '2px' }}>
                        Percentile: {dataPoint.percentile.toFixed(0)}%
                    </Typography>
                )}
                {dataPoint.session && (
                    <Typography sx={{
                        color: dataPoint.sessionColor || '#6b7280',
                        fontSize: '11px',
                        marginTop: '2px'
                    }}>
                        Session: {dataPoint.session}
                    </Typography>
                )}
            </Box>
        );
    };

    // Determine Y-axis domain based on mode
    const yAxisDomain = useMemo(() => {
        if (mode === 'zscore') {
            return [-3, 3]; // Fixed for Z-Score
        }
        if (data.length === 0) return ['auto', 'auto'];

        // Auto-calculate with 5% padding
        const values = data.map(d => d.value).filter(v => typeof v === 'number' && !isNaN(v));
        if (values.length === 0) return ['auto', 'auto'];

        const min = Math.min(...values);
        const max = Math.max(...values);
        const padding = (max - min) * 0.05;

        return [min - padding, max + padding];
    }, [mode, data]);

    // Empty state
    if (!data || data.length === 0) {
        return (
            <Box sx={{
                height: height,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#141414',
                borderRadius: '8px',
                border: '1px solid #262626'
            }}>
                <Typography sx={{ color: '#6b7280', fontSize: '14px' }}>
                    No chart data available
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            {/* Chart Mode Toggle */}
            {onModeChange && (
                <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: '4px' }}>
                    <ToggleButtonGroup
                        value={mode}
                        exclusive
                        onChange={(_, newMode) => {
                            if (newMode !== null) {
                                onModeChange(newMode);
                            }
                        }}
                        size="small"
                        sx={{
                            '& .MuiToggleButton-root': {
                                color: 'rgba(156, 163, 175, 0.85)',
                                fontSize: '11px',
                                padding: '4px 12px',
                                border: '1px solid rgba(6, 182, 212, 0.4)',
                                minWidth: 'auto',
                                lineHeight: '1.4',
                                fontWeight: '700',
                                letterSpacing: '0.4px',
                                textTransform: 'uppercase',
                                '&.Mui-selected': {
                                    backgroundColor: 'rgba(6, 182, 212, 0.25)',
                                    color: '#06b6d4',
                                    '&:hover': {
                                        backgroundColor: 'rgba(6, 182, 212, 0.35)',
                                    }
                                },
                                '&:hover': {
                                    backgroundColor: 'rgba(6, 182, 212, 0.1)'
                                }
                            }
                        }}
                    >
                        <ToggleButton value="percentile">%ile</ToggleButton>
                        <ToggleButton value="zscore">Z-Score</ToggleButton>
                        <ToggleButton value="spread">Spread</ToggleButton>
                        <ToggleButton value="session">Session</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            )}

            {/* Chart Container */}
            <Box sx={{
                width: '100%',
                height: height,
                backgroundColor: '#141414',
                borderRadius: '8px',
                border: '1px solid #262626',
                padding: '10px 6px'
            }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={data}
                        margin={{ top: 20, right: 20, left: 10, bottom: 10 }}
                    >
                        {/* Grid */}
                        <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="rgba(38, 38, 38, 0.5)"
                            vertical={false}
                            horizontal={true}
                        />

                        {/* X-Axis (Time) */}
                        <XAxis
                            dataKey="time"
                            stroke="#6b7280"
                            style={{
                                fontSize: '11px',
                                fontFamily: 'Courier New, monospace',
                                fontWeight: '500'
                            }}
                            tickLine={false}
                            interval="preserveStartEnd"
                        />

                        {/* Y-Axis (Value) */}
                        <YAxis
                            stroke="#6b7280"
                            style={{
                                fontSize: '11px',
                                fontFamily: 'Courier New, monospace',
                                fontWeight: '500'
                            }}
                            tickLine={false}
                            domain={yAxisDomain}
                            tickFormatter={(value) => value.toFixed(2)}
                        />

                        {/* Tooltip */}
                        <Tooltip content={<CustomTooltip />} />

                        {/* Reference Lines (P25/P50/P75 with simple text labels) */}
                        {referenceLines.map((line, index) => (
                            <ReferenceLine
                                key={index}
                                y={line.y}
                                stroke={line.stroke}
                                strokeDasharray={line.strokeDasharray}
                                strokeWidth={line.strokeWidth || 1}
                                label={<PercentileLabel value={line.label} stroke={line.stroke} />}
                            />
                        ))}

                        {/* Current Spread Indicator (horizontal line) */}
                        {currentSpread && mode === 'percentile' && (
                            <ReferenceLine
                                y={currentSpread}
                                stroke="#ffffff"
                                strokeDasharray="5 5"
                                strokeWidth={1}
                                label={<CurrentLabel value="CURRENT" />}
                            />
                        )}

                        {/* Line Chart */}
                        <Line
                            type="monotone"
                            dataKey="value"
                            stroke={lineColor}
                            strokeWidth={3}
                            dot={{
                                r: 2,
                                fill: lineColor,
                                strokeWidth: 0
                            }}
                            activeDot={{
                                r: 5,
                                fill: lineColor,
                                stroke: '#ffffff',
                                strokeWidth: 2
                            }}
                            animationDuration={300}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </Box>

            {/* Chart Info Footer - Static Labels, Dynamic Numbers (No Flickering) */}
            <Box sx={{
                marginTop: '4px',
                padding: '5px 10px',
                borderTop: '1px solid #262626',
                backgroundColor: '#0a0a0a',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px',
                minHeight: '26px'
            }}>
                {percentileBands && currentSpread ? (
                    <>
                        {/* P25 - Static label + Dynamic value */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Typography sx={{ color: '#22c55e', fontSize: '9px', fontWeight: '700', fontFamily: 'Courier New' }}>
                                P25
                            </Typography>
                            <Typography sx={{ color: '#22c55e', fontSize: '11px', fontWeight: '800', fontFamily: 'Courier New' }}>
                                {typeof percentileBands.p25 === 'number' ? percentileBands.p25.toFixed(0) : '-'}
                            </Typography>
                        </Box>

                        <span style={{ color: '#6b7280', fontSize: '11px' }}>|</span>

                        {/* MED - Static label + Dynamic value */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Typography sx={{ color: '#f59e0b', fontSize: '9px', fontWeight: '700', fontFamily: 'Courier New' }}>
                                MED
                            </Typography>
                            <Typography sx={{ color: '#f59e0b', fontSize: '11px', fontWeight: '800', fontFamily: 'Courier New' }}>
                                {typeof percentileBands.p50 === 'number' ? percentileBands.p50.toFixed(0) : '-'}
                            </Typography>
                        </Box>

                        <span style={{ color: '#6b7280', fontSize: '11px' }}>|</span>

                        {/* P75 - Static label + Dynamic value */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Typography sx={{ color: '#ef4444', fontSize: '9px', fontWeight: '700', fontFamily: 'Courier New' }}>
                                P75
                            </Typography>
                            <Typography sx={{ color: '#ef4444', fontSize: '11px', fontWeight: '800', fontFamily: 'Courier New' }}>
                                {typeof percentileBands.p75 === 'number' ? percentileBands.p75.toFixed(0) : '-'}
                            </Typography>
                        </Box>

                        {/* Beta - Static label + Dynamic value */}
                        {typeof currentBeta === 'number' && (
                            <>
                                <span style={{ color: '#6b7280', fontSize: '11px' }}>|</span>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Typography sx={{ color: '#a78bfa', fontSize: '9px', fontWeight: '700', fontFamily: 'Courier New' }}>
                                        β
                                    </Typography>
                                    <Typography sx={{ color: '#a78bfa', fontSize: '11px', fontWeight: '800', fontFamily: 'Courier New' }}>
                                        {currentBeta.toFixed(3)}
                                    </Typography>
                                </Box>
                            </>
                        )}

                        {/* Beta High - Static label + Dynamic value */}
                        {dailyStats && typeof dailyStats.betaHigh === 'number' && (
                            <>
                                <span style={{ color: '#6b7280', fontSize: '11px' }}>|</span>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Typography sx={{ color: '#22c55e', fontSize: '9px', fontWeight: '700', fontFamily: 'Courier New' }}>
                                        βH
                                    </Typography>
                                    <Typography sx={{ color: '#22c55e', fontSize: '11px', fontWeight: '800', fontFamily: 'Courier New' }}>
                                        {dailyStats.betaHigh.toFixed(3)}
                                    </Typography>
                                </Box>
                            </>
                        )}

                        {/* Beta Low - Static label + Dynamic value */}
                        {dailyStats && typeof dailyStats.betaLow === 'number' && (
                            <>
                                <span style={{ color: '#6b7280', fontSize: '11px' }}>|</span>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Typography sx={{ color: '#ef4444', fontSize: '9px', fontWeight: '700', fontFamily: 'Courier New' }}>
                                        βL
                                    </Typography>
                                    <Typography sx={{ color: '#ef4444', fontSize: '11px', fontWeight: '800', fontFamily: 'Courier New' }}>
                                        {dailyStats.betaLow.toFixed(3)}
                                    </Typography>
                                </Box>
                            </>
                        )}
                    </>
                ) : (
                    <Typography sx={{
                        color: '#6b7280',
                        fontStyle: 'italic',
                        fontSize: '10px',
                        fontFamily: 'Courier New, monospace'
                    }}>
                        Loading percentile data...
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

StrategyMiniChart.propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({
        time: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
        timestamp: PropTypes.string,
        percentile: PropTypes.number,
        session: PropTypes.string,
        sessionColor: PropTypes.string,
        label: PropTypes.string
    })),
    mode: PropTypes.oneOf(['percentile', 'zscore', 'spread', 'session']),
    onModeChange: PropTypes.func,
    percentileBands: PropTypes.shape({
        p1: PropTypes.number,
        p5: PropTypes.number,
        p10: PropTypes.number,
        p25: PropTypes.number,
        p50: PropTypes.number,
        p75: PropTypes.number,
        p90: PropTypes.number,
        p95: PropTypes.number,
        p99: PropTypes.number
    }),
    dailyStats: PropTypes.shape({
        spreadHigh: PropTypes.number,
        spreadLow: PropTypes.number,
        betaHigh: PropTypes.number,
        betaLow: PropTypes.number,
        correlationHigh: PropTypes.number,
        correlationLow: PropTypes.number,
        zScoreHigh: PropTypes.number,
        zScoreLow: PropTypes.number
    }),
    height: PropTypes.number,
    currentSpread: PropTypes.number,
    currentBeta: PropTypes.number,
    lineColor: PropTypes.string
};

// Memoize StrategyMiniChart to prevent unnecessary re-renders and flickering
export default React.memo(StrategyMiniChart, (prevProps, nextProps) => {
    // Only re-render if critical display values actually changed

    // Check data array (compare length and last element for real-time updates)
    const dataChanged = prevProps.data.length !== nextProps.data.length ||
        (prevProps.data.length > 0 && nextProps.data.length > 0 &&
         prevProps.data[prevProps.data.length - 1] !== nextProps.data[nextProps.data.length - 1]);

    // Check mode
    const modeChanged = prevProps.mode !== nextProps.mode;

    // Check percentile bands (deep compare key values)
    const prevBands = prevProps.percentileBands;
    const nextBands = nextProps.percentileBands;
    const bandsChanged = (
        prevBands?.p25 !== nextBands?.p25 ||
        prevBands?.p50 !== nextBands?.p50 ||
        prevBands?.p75 !== nextBands?.p75
    );

    // Check dailyStats (deep compare Beta H/L)
    const prevStats = prevProps.dailyStats;
    const nextStats = nextProps.dailyStats;
    const statsChanged = (
        prevStats?.betaHigh !== nextStats?.betaHigh ||
        prevStats?.betaLow !== nextStats?.betaLow
    );

    // Check current values
    const currentChanged = (
        prevProps.currentSpread !== nextProps.currentSpread ||
        prevProps.currentBeta !== nextProps.currentBeta
    );

    // Check other props
    const otherChanged = (
        prevProps.height !== nextProps.height ||
        prevProps.lineColor !== nextProps.lineColor ||
        prevProps.onModeChange !== nextProps.onModeChange
    );

    // Return true if NO changes (skip re-render), false if changed (allow re-render)
    return !dataChanged && !modeChanged && !bandsChanged && !statsChanged && !currentChanged && !otherChanged;
});
